import { useEffect, useRef } from "react";

// Lightweight WebGL flowing-aurora background (single fragment shader, no libraries).
// - Pauses when offscreen (IntersectionObserver) or when the tab is hidden.
// - Renders one static frame under prefers-reduced-motion, Save-Data, or low-power devices.
// - Falls back to the CSS gradient behind it when WebGL is unavailable.
const FRAG = `
precision mediump float;
uniform vec2 r; uniform float t; uniform float dark;
float h(vec2 p){return fract(sin(dot(p,vec2(127.1,311.7)))*43758.5453);}
float n(vec2 p){vec2 i=floor(p),f=fract(p);f=f*f*(3.-2.*f);
 return mix(mix(h(i),h(i+vec2(1,0)),f.x),mix(h(i+vec2(0,1)),h(i+vec2(1,1)),f.x),f.y);}
float fbm(vec2 p){float v=0.,a=.5;for(int i=0;i<5;i++){v+=a*n(p);p=p*2.03+vec2(1.7,9.2);a*=.5;}return v;}
void main(){
 vec2 uv=gl_FragCoord.xy/r; vec2 p=uv*vec2(r.x/r.y,1.)*1.6;
 float T=t*.07;
 vec2 q=vec2(fbm(p+vec2(0.,T)),fbm(p+vec2(5.2,1.3)-T));
 vec2 w=vec2(fbm(p+3.*q+vec2(1.7,9.2)+T*1.3),fbm(p+3.*q+vec2(8.3,2.8)-T));
 float f=fbm(p+2.5*w);
 vec3 navy=vec3(.06,.165,.29), blue=vec3(.18,.48,.82), teal=vec3(.08,.72,.65);
 vec3 c;
 if(dark>.5){
  c=mix(vec3(.035,.10,.19),navy,smoothstep(.2,.8,f));
  c=mix(c,blue*.75,smoothstep(.45,.95,w.x)*.55);
  c=mix(c,teal*.8,smoothstep(.55,1.,q.y)*.5);
 } else {
  vec3 base=vec3(.985,.99,1.);
  c=base;
  c=mix(c,vec3(.74,.85,.98),smoothstep(.30,.80,f));
  c=mix(c,vec3(.55,.86,.82),smoothstep(.45,.90,w.x)*.95);
  c=mix(c,vec3(.52,.70,.95),smoothstep(.50,.95,q.y)*.9);
  c=mix(c,vec3(.33,.52,.80),smoothstep(.70,1.,f*w.y*1.6)*.55);
  float band=smoothstep(-.1,.75,uv.x);
  c=mix(base,c,.30+.70*band);
 }
 gl_FragColor=vec4(c,1.);
}`;
const VERT = "attribute vec2 a;void main(){gl_Position=vec4(a,0.,1.);}";

export function AuroraCanvas({ dark = false, className = "" }) {
    const ref = useRef(null);
    useEffect(() => {
        const canvas = ref.current;
        if (!canvas) return undefined;
        const gl = canvas.getContext("webgl", { antialias: false, alpha: false, powerPreference: "low-power" });
        if (!gl) { canvas.style.display = "none"; return undefined; }
        const sh = (type, src) => { const s = gl.createShader(type); gl.shaderSource(s, src); gl.compileShader(s); return s; };
        const prog = gl.createProgram();
        gl.attachShader(prog, sh(gl.VERTEX_SHADER, VERT)); gl.attachShader(prog, sh(gl.FRAGMENT_SHADER, FRAG)); gl.linkProgram(prog);
        if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) { canvas.style.display = "none"; return undefined; }
        gl.useProgram(prog);
        const buf = gl.createBuffer(); gl.bindBuffer(gl.ARRAY_BUFFER, buf);
        gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
        const loc = gl.getAttribLocation(prog, "a"); gl.enableVertexAttribArray(loc); gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
        const uR = gl.getUniformLocation(prog, "r"), uT = gl.getUniformLocation(prog, "t"), uD = gl.getUniformLocation(prog, "dark");
        gl.uniform1f(uD, dark ? 1 : 0);

        const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
        const saveData = navigator.connection?.saveData;
        const lowPower = (navigator.hardwareConcurrency || 8) <= 2;
        const animate = !(reduce || saveData || lowPower);
        canvas.dataset.animated = String(animate);

        const SCALE = 0.5; // render at half resolution; the blur-like aurora upscales cleanly
        const resize = () => {
            const w = Math.max(1, Math.floor(canvas.clientWidth * SCALE)), h = Math.max(1, Math.floor(canvas.clientHeight * SCALE));
            if (canvas.width !== w || canvas.height !== h) { canvas.width = w; canvas.height = h; gl.viewport(0, 0, w, h); gl.uniform2f(uR, w, h); }
        };
        let raf = 0, visible = true, start = performance.now(), offset = 18;
        const draw = (now) => { resize(); gl.uniform1f(uT, offset + (now - start) / 1000); gl.drawArrays(gl.TRIANGLES, 0, 3); };
        const loop = (now) => { draw(now); raf = requestAnimationFrame(loop); };
        const play = () => { if (animate && visible && !document.hidden && !raf) { canvas.dataset.running = "true"; raf = requestAnimationFrame(loop); } };
        const pause = () => { if (raf) { cancelAnimationFrame(raf); raf = 0; } canvas.dataset.running = "false"; };
        draw(performance.now());
        const io = new IntersectionObserver(([e]) => { visible = e.isIntersecting; if (visible) play(); else pause(); });
        io.observe(canvas);
        const onVis = () => (document.hidden ? pause() : play());
        document.addEventListener("visibilitychange", onVis);
        const onResize = () => { if (!raf) draw(performance.now()); };
        window.addEventListener("resize", onResize);
        play();
        return () => { pause(); io.disconnect(); document.removeEventListener("visibilitychange", onVis); window.removeEventListener("resize", onResize); };
    }, [dark]);
    return <canvas ref={ref} className={`absolute inset-0 h-full w-full ${className}`} aria-hidden="true" data-testid={dark ? "aurora-dark" : "aurora-hero"} />;
}
